import { useState, type MouseEvent } from 'react'
import { ChevronDown, ChevronRight, FlaskConical, Layers, Network, Building2 } from 'lucide-react'
import { Checkbox } from '@/shared/ui/checkbox'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/shared/ui/alert-dialog'
import { t } from '@/shared/lib/i18n'

export interface RestorePreviewInstrument {
  id: number
  name: string
}

export interface RestorePreviewUnit {
  id: number
  name: string
  discarded_instruments: RestorePreviewInstrument[]
}

export interface RestorePreviewDepartment {
  id: number
  name: string
  discarded_units: RestorePreviewUnit[]
}

export interface RestoreSelection {
  departmentIds: number[]
  unitIds: number[]
  instrumentIds: number[]
}

interface RestoreDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  resourceName: string
  loading?: boolean
  departments?: RestorePreviewDepartment[]
  units?: RestorePreviewUnit[]
  instruments?: RestorePreviewInstrument[]
  onConfirm: (selection: RestoreSelection) => void
}

const InstrumentRow = ({
  instrument,
  checked,
  onToggle,
}: {
  instrument: RestorePreviewInstrument
  checked: boolean
  onToggle: (id: number) => void
}) => (
  <div className="flex items-center gap-2 py-1.5">
    <Checkbox
      id={`instrument-${instrument.id}`}
      checked={checked}
      onCheckedChange={() => onToggle(instrument.id)}
      className="shrink-0"
    />
    <FlaskConical className="h-3.5 w-3.5 shrink-0 text-muted-foreground/60" strokeWidth={1.75} />
    <label
      htmlFor={`instrument-${instrument.id}`}
      className="cursor-pointer text-sm text-muted-foreground leading-none"
    >
      {instrument.name}
    </label>
  </div>
)

const UnitRow = ({
  unit,
  checked,
  instrumentIds,
  onToggleUnit,
  onToggleInstrument,
  defaultExpanded = true,
}: {
  unit: RestorePreviewUnit
  checked: boolean
  instrumentIds: Set<number>
  onToggleUnit: (id: number) => void
  onToggleInstrument: (id: number) => void
  defaultExpanded?: boolean
}) => {
  const [expanded, setExpanded] = useState(defaultExpanded)
  const hasInstruments = unit.discarded_instruments.length > 0

  return (
    <div>
      <div className="flex items-center gap-2 py-1.5">
        <Checkbox
          id={`unit-${unit.id}`}
          checked={checked}
          onCheckedChange={() => onToggleUnit(unit.id)}
          className="shrink-0"
        />
        <button
          type="button"
          onClick={() => hasInstruments && setExpanded((v) => !v)}
          className={`flex h-5 w-5 shrink-0 items-center justify-center rounded text-muted-foreground ${hasInstruments ? 'hover:bg-muted' : 'cursor-default'}`}
        >
          {hasInstruments &&
            (expanded ? (
              <ChevronDown className="h-3.5 w-3.5" />
            ) : (
              <ChevronRight className="h-3.5 w-3.5" />
            ))}
        </button>
        <Layers className="h-4 w-4 shrink-0 text-muted-foreground" strokeWidth={1.75} />
        <label
          htmlFor={`unit-${unit.id}`}
          className="cursor-pointer text-sm font-medium leading-none"
        >
          {unit.name}
        </label>
      </div>

      {expanded && hasInstruments && (
        <div className="ml-[3.25rem] border-l-2 border-border/50 pl-3 space-y-0">
          {unit.discarded_instruments.map((instrument) => (
            <InstrumentRow
              key={instrument.id}
              instrument={instrument}
              checked={instrumentIds.has(instrument.id)}
              onToggle={onToggleInstrument}
            />
          ))}
        </div>
      )}
    </div>
  )
}

const DepartmentSection = ({
  dept,
  checked,
  unitIds,
  instrumentIds,
  onToggleDept,
  onToggleUnit,
  onToggleInstrument,
}: {
  dept: RestorePreviewDepartment
  checked: boolean
  unitIds: Set<number>
  instrumentIds: Set<number>
  onToggleDept: (id: number) => void
  onToggleUnit: (id: number) => void
  onToggleInstrument: (id: number) => void
}) => {
  const [expanded, setExpanded] = useState(true)
  const hasUnits = dept.discarded_units.length > 0

  return (
    <div>
      <div className="flex items-center gap-2 py-1.5">
        <Checkbox
          id={`dept-${dept.id}`}
          checked={checked}
          onCheckedChange={() => onToggleDept(dept.id)}
          className="shrink-0"
        />
        <button
          type="button"
          onClick={() => hasUnits && setExpanded((v) => !v)}
          className={`flex h-5 w-5 shrink-0 items-center justify-center rounded text-muted-foreground ${hasUnits ? 'hover:bg-muted' : 'cursor-default'}`}
        >
          {hasUnits &&
            (expanded ? (
              <ChevronDown className="h-3.5 w-3.5" />
            ) : (
              <ChevronRight className="h-3.5 w-3.5" />
            ))}
        </button>
        <Network className="h-4 w-4 shrink-0 text-muted-foreground" strokeWidth={1.75} />
        <label
          htmlFor={`dept-${dept.id}`}
          className="cursor-pointer text-sm font-semibold leading-none"
        >
          {dept.name}
        </label>
      </div>

      {expanded && hasUnits && (
        <div className="ml-[3.25rem] border-l-2 border-border pl-3 space-y-0">
          {dept.discarded_units.map((unit) => (
            <UnitRow
              key={unit.id}
              unit={unit}
              checked={unitIds.has(unit.id)}
              instrumentIds={instrumentIds}
              onToggleUnit={onToggleUnit}
              onToggleInstrument={onToggleInstrument}
            />
          ))}
        </div>
      )}
    </div>
  )
}

export const RestoreDialog = ({
  open,
  onOpenChange,
  resourceName,
  loading = false,
  departments,
  units,
  instruments,
  onConfirm,
}: RestoreDialogProps) => {
  const [selectedDepartmentIds, setSelectedDepartmentIds] = useState<Set<number>>(new Set())
  const [selectedUnitIds, setSelectedUnitIds] = useState<Set<number>>(new Set())
  const [selectedInstrumentIds, setSelectedInstrumentIds] = useState<Set<number>>(new Set())
  const [validationError, setValidationError] = useState<string | null>(null)

  const resetSelection = () => {
    setSelectedDepartmentIds(new Set())
    setSelectedUnitIds(new Set())
    setSelectedInstrumentIds(new Set())
    setValidationError(null)
  }

  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen) resetSelection()
    onOpenChange(isOpen)
  }

  const toggleId = (set: Set<number>, id: number): Set<number> => {
    setValidationError(null)
    const next = new Set(set)
    if (next.has(id)) next.delete(id)
    else next.add(id)
    return next
  }

  const allDeptIds = departments?.map((d) => d.id) ?? []
  const allUnitIds = [
    ...(departments?.flatMap((d) => d.discarded_units.map((u) => u.id)) ?? []),
    ...(units?.map((u) => u.id) ?? []),
  ]
  const allInstrumentIds = [
    ...(departments?.flatMap((d) =>
      d.discarded_units.flatMap((u) => u.discarded_instruments.map((i) => i.id)),
    ) ?? []),
    ...(units?.flatMap((u) => u.discarded_instruments.map((i) => i.id)) ?? []),
    ...(instruments?.map((i) => i.id) ?? []),
  ]

  const totalAll = allDeptIds.length + allUnitIds.length + allInstrumentIds.length
  const totalSelected =
    selectedDepartmentIds.size + selectedUnitIds.size + selectedInstrumentIds.size

  const isAllSelected = totalAll > 0 && totalSelected === totalAll
  const isPartiallySelected = totalSelected > 0 && totalSelected < totalAll

  const handleSelectAll = () => {
    setValidationError(null)
    if (isAllSelected) {
      setSelectedDepartmentIds(new Set())
      setSelectedUnitIds(new Set())
      setSelectedInstrumentIds(new Set())
    } else {
      setSelectedDepartmentIds(new Set(allDeptIds))
      setSelectedUnitIds(new Set(allUnitIds))
      setSelectedInstrumentIds(new Set(allInstrumentIds))
    }
  }

  const handleConfirm = (event: MouseEvent<HTMLButtonElement>) => {
    if (!isSelectionConsistent()) {
      // AlertDialogAction closes the dialog by default; block it so the error stays visible.
      event.preventDefault()
      setValidationError(t('restoreDialog.parentRequired'))
      return
    }
    onConfirm({
      departmentIds: [...selectedDepartmentIds],
      unitIds: [...selectedUnitIds],
      instrumentIds: [...selectedInstrumentIds],
    })
    handleOpenChange(false)
  }

  // A child can only be restored when its (deactivated) parent is also selected: units
  // shown under a department require that department, instruments require their unit.
  const isSelectionConsistent = (): boolean => {
    const unitRequiresDepartment = departments?.every((dept) =>
      dept.discarded_units.every(
        (unit) => !selectedUnitIds.has(unit.id) || selectedDepartmentIds.has(dept.id),
      ),
    )

    const instrumentGroups = [
      ...(departments?.flatMap((d) => d.discarded_units) ?? []),
      ...(units ?? []),
    ]
    const instrumentRequiresUnit = instrumentGroups.every((unit) =>
      unit.discarded_instruments.every(
        (instrument) => !selectedInstrumentIds.has(instrument.id) || selectedUnitIds.has(unit.id),
      ),
    )

    return (unitRequiresDepartment ?? true) && instrumentRequiresUnit
  }

  const hasChildren =
    (departments && departments.length > 0) ||
    (units && units.length > 0) ||
    (instruments && instruments.length > 0)

  return (
    <AlertDialog open={open} onOpenChange={handleOpenChange}>
      <AlertDialogContent className="max-w-lg">
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            <Building2 className="h-5 w-5 text-muted-foreground" strokeWidth={1.75} />
            {t('restoreDialog.title')}: {resourceName}
          </AlertDialogTitle>
          <AlertDialogDescription>
            {loading
              ? t('restoreDialog.loading')
              : hasChildren
                ? t('restoreDialog.descriptionWithChildren')
                : t('restoreDialog.descriptionEmpty')}
          </AlertDialogDescription>
        </AlertDialogHeader>

        {!loading && hasChildren && (
          <div className="rounded-md border bg-card">
            <div className="flex items-center gap-2 border-b px-3 py-2">
              <Checkbox
                id="select-all"
                checked={isPartiallySelected ? 'indeterminate' : isAllSelected}
                onCheckedChange={handleSelectAll}
                className="shrink-0"
              />
              <label
                htmlFor="select-all"
                className="cursor-pointer text-sm font-medium leading-none"
              >
                {t('restoreDialog.selectAll')}
              </label>
            </div>
            <div className="max-h-64 overflow-y-auto px-3 py-2 space-y-1">
              {/* Site → Departments → Units → Instruments */}
              {departments?.map((dept) => (
                <DepartmentSection
                  key={dept.id}
                  dept={dept}
                  checked={selectedDepartmentIds.has(dept.id)}
                  unitIds={selectedUnitIds}
                  instrumentIds={selectedInstrumentIds}
                  onToggleDept={(id) =>
                    setSelectedDepartmentIds(toggleId(selectedDepartmentIds, id))
                  }
                  onToggleUnit={(id) => setSelectedUnitIds(toggleId(selectedUnitIds, id))}
                  onToggleInstrument={(id) =>
                    setSelectedInstrumentIds(toggleId(selectedInstrumentIds, id))
                  }
                />
              ))}

              {/* Department → Units → Instruments */}
              {units?.map((unit) => (
                <UnitRow
                  key={unit.id}
                  unit={unit}
                  checked={selectedUnitIds.has(unit.id)}
                  instrumentIds={selectedInstrumentIds}
                  onToggleUnit={(id) => setSelectedUnitIds(toggleId(selectedUnitIds, id))}
                  onToggleInstrument={(id) =>
                    setSelectedInstrumentIds(toggleId(selectedInstrumentIds, id))
                  }
                />
              ))}

              {/* Unit → Instruments */}
              {instruments?.map((instrument) => (
                <InstrumentRow
                  key={instrument.id}
                  instrument={instrument}
                  checked={selectedInstrumentIds.has(instrument.id)}
                  onToggle={(id) => setSelectedInstrumentIds(toggleId(selectedInstrumentIds, id))}
                />
              ))}

              <p className="border-t pt-2 text-xs text-muted-foreground">
                {t('restoreDialog.tasksAutoRestored')}
              </p>
            </div>
          </div>
        )}

        {validationError && (
          <p className="text-sm text-destructive" role="alert">
            {validationError}
          </p>
        )}

        <AlertDialogFooter>
          <AlertDialogCancel>{t('restoreDialog.cancel')}</AlertDialogCancel>
          <AlertDialogAction onClick={handleConfirm} disabled={loading}>
            {t('restoreDialog.confirm')}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}
